import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Warehouse,
    Layers,
    Landmark,
    CheckCircle2,
    TrendingUp,
    TrendingDown,
    MapPin,
    X,
    ChevronDown,
    Cctv,
    Clock,
    Play,
    ArrowRight,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
// import { ZONE_TONES } from '../config/zoneTones';
import { zoneLabel } from '../utils/zoneLabel';
import baseapi, { API_BASE } from '../baseapi';
import { LiveFrame } from '../components/junctions/LiveFrame';
import { BoxVideo } from '../components/holding/BoxVideo';
import { DevoteeTrend, type DevoteeTrendMetrics } from '../components/overview/DevoteeTrend';
import { FestivalTrend } from '../components/overview/FestivalTrend';
import { DarshanCompletedDetails } from '../components/overview/DarshanCompletedDetails';

declare global {
    interface Window {
        google?: any;
        __overviewGmapsReady?: () => void;
    }
}

const DEFAULT_MAPS_KEY = "AIzaSyDRbkMHXDupqdh7KRy4RBV1VLqfN-xUBR8";

// Refined Google Maps Dark & Light Styles with High Contrast between Normal Roads and Inbound Corridors
const MAP_STYLE_DARK = [
    { elementType: "geometry", stylers: [{ color: "#0B1118" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#060A0E" }, { weight: 3 }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#64748B" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
    { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    // Normal Roads: Neutral Dark Slate (Muted so Inbound Corridors pop out)
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#172230" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#1E2D3D" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#24364A" }] },
    { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
    // Krishna River: Distinct Deep Blue
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#0B263B" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#38BDF8" }] },
    { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#0D1620" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#1E293B" }] }
];

const MAP_STYLE_LIGHT = [
    { elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }, { weight: 3 }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#64748B" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
    { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    // Normal Roads: Clean Muted Gray
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#E2E8F0" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#94A3B8" }] },
    { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
    // Krishna River: Clear Soft Blue
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0284C7" }] },
    { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] }
];

// Precision Coordinates
const TEMPLE_COORD = { lat: 16.51322, lng: 80.60376 };
// Junction Sequential Flow & Transit Monitoring on the map (13 Oct 2026): transit status of a link / node
const JF_TRANSIT: Record<string, { text: string; color: string }> = {
    moving: { text: 'Moving', color: '#10b981' }, building_up: { text: 'Building up', color: '#f59e0b' },
    other_inflow: { text: 'Other inflow', color: '#38bdf8' }, quiet: { text: 'Quiet', color: '#94a3b8' },
    no_data: { text: 'No data', color: '#64748b' },
};
const JF_STATE_TEXT: Record<string, string> = {
    online: 'Cameras online', partial: 'Some cameras down', offline: 'Camera offline', not_counting: 'Not counting', no_camera: 'No camera',
};
const JF_STATE_DOT: Record<string, string> = {
    online: '#10b981', partial: '#f59e0b', offline: '#ef4444', not_counting: '#f87171', no_camera: '#94a3b8',
};
const JF_KIND_COLOR: Record<string, string> = {
    rail: '#34d399', bus: '#fbbf24', ghats: '#38bdf8', junction: '#2dd4bf', park: '#5eead4', holding: '#facc15',
    queue: '#a78bfa', temple: '#fb923c', exit: '#fb7185', dispersal: '#fda4af',
};
const RAILWAY_STN_COORD = { lat: 16.5183, lng: 80.6199 };
const PNBS_BUS_COORD = { lat: 16.5087, lng: 80.6158 };

const HYD_ENTRY_COORD = { lat: 16.5450, lng: 80.5650 };
const GNT_ENTRY_COORD = { lat: 16.4720, lng: 80.6350 };
const ELR_ENTRY_COORD = { lat: 16.5450, lng: 80.6750 };

// Inbound Road Waypoints following actual highway corridors to temple
const HYD_ROAD_POINTS = [
    HYD_ENTRY_COORD,
    { lat: 16.5360, lng: 80.5780 },
    { lat: 16.5280, lng: 80.5890 },
    { lat: 16.5210, lng: 80.5970 },
    { lat: 16.5150, lng: 80.6025 },
    TEMPLE_COORD
];

const GNT_ROAD_POINTS = [
    GNT_ENTRY_COORD,
    { lat: 16.4850, lng: 80.6280 },
    { lat: 16.4980, lng: 80.6170 },
    { lat: 16.5070, lng: 80.6110 },
    { lat: 16.5115, lng: 80.6060 },
    TEMPLE_COORD
];

const ELR_ROAD_POINTS = [
    ELR_ENTRY_COORD,
    { lat: 16.5350, lng: 80.6520 },
    { lat: 16.5250, lng: 80.6350 },
    { lat: 16.5180, lng: 80.6180 },
    { lat: 16.5140, lng: 80.6080 },
    TEMPLE_COORD
];

// Railway track points
const RAILWAY_LINE_POINTS = [
    { lat: 16.5480, lng: 80.6480 },
    { lat: 16.5320, lng: 80.6320 },
    RAILWAY_STN_COORD,
    { lat: 16.5110, lng: 80.6110 },
    { lat: 16.5020, lng: 80.6030 },
    { lat: 16.4880, lng: 80.5960 }
];

// Queue zone as shown on this page. Every figure comes from GET /api/overview; null = not measured
// (no camera for it yet) and is shown as "—", never a sample number.
/** One colour per queue zone (route order): header band, card tint, border, number, chip and bar. */

interface QueueZoneData {
    id: string;
    label: string;             // shown on screen: Zone 1, Zone 2 ... in route order (the id stays the key in the config)
    name: string;
    count: number | null;      // people the zone's cameras see right now
    density: number | null;
    inflow: number | null;     // people per minute past the zone's line (15-minute average)
    outflow: number | null;
    dwellTime: number | null;
    estWait: number | null;
    entryNow: number | null;   // crossings recorded in the last 60 seconds
    exitNow: number | null;
    passed: number | null;     // people counted past the zone's line today
    hasCam: boolean;
    statusColor: 'emerald' | 'amber' | 'rose' | 'teal';
    entryCams: ZoneCam[];      // the cameras people come into the zone by, and the ones they leave it by (from the server)
    exitCams: ZoneCam[];
}

/** One camera of a queue zone, as GET /api/overview gives it in entry_cameras / exit_cameras. */
interface ZoneCam { id: string; name: string; ip?: string | null; state?: string | null }

/**
 * The analytics of one camera, shown beside its live picture: people in its view now, people counted past its
 * line today and in the last 30 minutes, and how fast it is analysed. A queue camera is one-way, so "passed" is
 * the busier direction of its line and "other way" the people who crossed it back.
 * GET /api/cameras/{id}/status (every 3 s), /api/cameras/{id}/counts and /api/cameras/overview (every 15 s).
 */
function ZoneCamStats({ camId }: { camId: string }) {
    const [status, setStatus] = useState<any | null>(null);
    const [recent, setRecent] = useState<{ toward: number; away: number } | null>(null);
    const [today, setToday] = useState<{ toward: number; away: number } | null>(null);
    useEffect(() => {
        let alive = true;
        setStatus(null); setRecent(null); setToday(null);
        const fast = () => baseapi.get(`cameras/${encodeURIComponent(camId)}/status`).then(r => { if (alive) setStatus(r.data); }).catch(() => { /* keep */ });
        const slow = () => {
            baseapi.get(`cameras/${encodeURIComponent(camId)}/counts`).then(r => { if (alive && r.data?.total) setRecent(r.data.total); }).catch(() => { /* keep */ });
            baseapi.get('cameras/overview').then(r => {
                const c = (r.data?.cameras ?? []).find((x: any) => x.id === camId);
                if (alive && c?.today) setToday({ toward: c.today.people_toward ?? 0, away: c.today.people_away ?? 0 });
            }).catch(() => { /* keep */ });
        };
        fast(); slow();
        const t1 = window.setInterval(fast, 2000), t2 = window.setInterval(slow, 5000);
        return () => { alive = false; window.clearInterval(t1); window.clearInterval(t2); };
    }, [camId]);
    const f = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-IN'));
    const hi = (d: { toward: number; away: number } | null) => (d ? Math.max(d.toward, d.away) : null);
    const lo = (d: { toward: number; away: number } | null) => (d ? Math.min(d.toward, d.away) : null);
    const counting = status?.analytics !== false;
    const cells: [string, string, string][] = [
        ['In view now', counting && status?.state === 'online' ? f(status?.tracks) : '—', 'people the camera sees'],
        ['Passed today', f(hi(today)), 'busier direction of its line'],
        ['Other way today', f(lo(today)), 'crossed the line back'],
        ['Last 30 min', f(hi(recent)), `other way ${f(lo(recent))}`],
        ['Analysed', counting && status?.proc_fps != null ? `${status.proc_fps} fps` : '—', counting ? 'pictures a second' : 'view only: not counted'],
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#262b37] border-t border-[#262b37]">
            {cells.map(([label, value, note]) => (
                <div key={label} className="bg-[#0f1218] px-3 py-2 min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wide text-neutral-600 dark:text-neutral-400 truncate">{label}</div>
                    <div className="text-xl sm:text-2xl font-black text-neutral-100 leading-tight tabular-nums">{value}</div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-400 truncate">{note}</div>
                </div>
            ))}
        </div>
    );
}

/** Place names without the camera addresses the server puts in them: "Tollgate (192.168.2.138)" -> "Tollgate". */
// also "(192.168.2.38 + 192.168.2.41)", and the short "Q lines" spelt out (15 Oct 2026)
const noAddress = (name: unknown) => String(name ?? '').replace(/\s*\((?:[\s.,+&]|\d)*\d(?:[\s.,+&]|\d)*\)/g, '')
    .replace(/\bQ lines\b/g, 'queue lines').replace(/\s{2,}/g, ' ').trim();

// Helper to determine occupancy tier
const getBoxTier = (current: number, max: number): 'available' | 'filling' | 'high' | 'full' => {
    if (!max) return 'available';
    const ratio = current / max;
    if (ratio < 0.5) return 'available';
    if (ratio <= 0.75) return 'filling';
    if (ratio <= 0.9) return 'high';
    return 'full';
};

const getBoxColorClasses = (tier: 'available' | 'filling' | 'high' | 'full') => {
    switch (tier) {
        case 'available':
            return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25';
        case 'filling':
            return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/25';
        case 'high':
            return 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30 hover:bg-orange-500/25';
        case 'full':
            return 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 hover:bg-rose-500/25';
    }
};

interface BoxItem {
    id: string;
    current: number | null;    // null = this box has no camera yet
    max: number;
}

const NO_CAM_BOX = 'bg-neutral-500/5 text-neutral-600 dark:text-neutral-500 border-neutral-300/60 dark:border-neutral-700/70 hover:bg-neutral-500/10';

export const OverviewPage: React.FC = () => {
    const { theme } = useTheme();
    const [festTableSlot, setFestTableSlot] = useState<HTMLDivElement | null>(null);   // where FestivalTrend puts its table
    const isDark = theme === 'dark';

    // Map View Mode: 'google' (Google Maps live) or 'satellite' or 'tactical' (dark vector schematic)
    const [mapMode, _setMapMode] = useState<'google' | 'satellite' | 'tactical'>('google');
    // Traffic OFF by default so normal roads are not covered with traffic colors!
    const [showTraffic, setShowTraffic] = useState<boolean>(false);

    const [activeBoxModal, setActiveBoxModal] = useState<{
        areaName: string;
        box: BoxItem;
        rawId?: string;      // the box's id in the holding API (HA1-B07), to look up its camera
    } | null>(null);
    // The box popup's occupancy, read again every second while the popup is open. activeBoxModal.box is the
    // figure at the moment of the click, so the popup used to show an older count than the "IN COUNTING AREA"
    // badge on its own live video, and drifted further the longer it stayed open (11 Oct 2026).
    const [liveBoxCount, setLiveBoxCount] = useState<number | null | undefined>(undefined);
    useEffect(() => {
        setLiveBoxCount(undefined);
        const id = activeBoxModal?.rawId;
        if (!id) return;
        let alive = true;
        const load = () => baseapi.get(`holding/boxes/${encodeURIComponent(id)}`)
            .then(r => { if (alive) setLiveBoxCount(r.data?.count ?? null); })
            .catch(() => { /* keep the last figure */ });
        load();
        const t = window.setInterval(load, 1000);
        return () => { alive = false; window.clearInterval(t); };
    }, [activeBoxModal?.rawId]);
    const boxNow: number | null = liveBoxCount !== undefined ? liveBoxCount : (activeBoxModal?.box.current ?? null);
    const [_selectedCityNode, setSelectedCityNode] = useState<string | null>(null);
    void _selectedCityNode;

    // ---- live data: GET /api/overview every 2 s. Camera-measured figures only; a block whose cameras
    // are not installed gets null from the server and shows "—" / "No camera" here.
    const [api, setApi] = useState<any | null>(null);
    const [overviewUpdatedAt, setOverviewUpdatedAt] = useState<number | null>(null);
    // Current-hour verified journey estimates from the Darshan Evidence summary.
    // Keep the last good result on a temporary request failure so this operational card does not flicker.
    const [currentDarshanLines, setCurrentDarshanLines] = useState<any | null>(null);
    // Keep the summary cards collapsed on first load; the button can show or hide them.
    const [showSummary, setShowSummary] = useState<boolean>(false);
    const [showDarshanCompleted, setShowDarshanCompleted] = useState(false);
    const [devoteeMetrics, setDevoteeMetrics] = useState<DevoteeTrendMetrics | null>(null);
    // the camera opened from a queue zone card (its Entry / Exit camera icons)
    const [zoneCam, setZoneCam] = useState<{ zone: string; zoneId?: string; side: 'Entry' | 'Exit'; cam: ZoneCam } | null>(null);
    // const [clock, setClock] = useState(() => new Date());
    useEffect(() => {
        let alive = true;
        let inFlight = false;
        // A refresh that fails or comes back incomplete keeps the figures already on screen: blanking them
        // would also rebuild the city map (its junction markers come from this data) and make the page flicker.
        const load = () => {
            if (inFlight) return;
            inFlight = true;
            baseapi.get('overview', { timeout: 30000 })
                .then(r => {
                    if (alive && r.data && Array.isArray(r.data.zones)) {
                        setApi(r.data);
                        setOverviewUpdatedAt(typeof r.data.server_time === 'number' ? r.data.server_time * 1000 : Date.now());
                    }
                })
                .catch(() => undefined)
                .finally(() => { inFlight = false; });
        };
        load();
        // auto refresh every 2 s; a hidden tab waits and catches up the moment it is shown again
        const t = window.setInterval(() => { if (!document.hidden) load(); }, 2000);
        const onShow = () => { if (!document.hidden) load(); };
        document.addEventListener('visibilitychange', onShow);
        return () => { alive = false; window.clearInterval(t); document.removeEventListener('visibilitychange', onShow); };
    }, []);

    useEffect(() => {
        let alive = true;
        let inFlight = false;
        const loadCurrentDarshan = () => {
            if (inFlight) return;
            inFlight = true;
            // The API returns the two Darshan Evidence values and their shared arithmetic average.
            baseapi.get('darshan/timings', { timeout: 30000 })
                .then(result => {
                    if (alive && result.data?.darshan_evidence_wait) setCurrentDarshanLines(result.data.darshan_evidence_wait);
                })
                .catch(() => undefined)
                .finally(() => { inFlight = false; });
        };
        loadCurrentDarshan();
        const timer = window.setInterval(() => { if (!document.hidden) loadCurrentDarshan(); }, 30000);
        const onShow = () => { if (!document.hidden) loadCurrentDarshan(); };
        document.addEventListener('visibilitychange', onShow);
        return () => {
            alive = false;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onShow);
        };
    }, []);

    const n = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString('en-IN'));
    const perMin = (v: number | null | undefined) => (v == null ? '—' : `${n(v)} / min`);
    const fmtMin = (m: number | null | undefined) => {
        if (m == null) return '—';
        const h = Math.floor(m / 60);
        const min = Math.round(m % 60);
        return h > 0 ? `${h} h ${min} m` : `${min} min`;
    };
    const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
    const rate = (v: number) => Math.round(v).toLocaleString('en-IN');
    const overviewUpdatedText = overviewUpdatedAt == null ? 'Connecting…' : new Date(overviewUpdatedAt).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
    });
    const k: any = api?.kpis ?? {};
    const vinayakaDarshanMin: number | null = currentDarshanLines?.vinayaka?.minutes ?? null;
    const displayDarshanMin: number | null = currentDarshanLines?.display_minutes ?? vinayakaDarshanMin;
    // const clockText = `${clock.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' })} · ${clock.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })} IST`;

    const cityNode = (id: string) => (api?.city?.nodes ?? []).find((x: any) => x.id === id);
    const nodeText = (id: string) => {
        const x = cityNode(id);
        return x && x.total != null ? `${n(x.total)} · ${n(x.rate)}/min` : 'No camera';
    };
    const junctionNodes: any[] = (api?.city?.nodes ?? []).filter((x: any) => x.kind === 'junction' && x.lat != null && x.lng != null);
    const junctionKey = junctionNodes.map(j => j.id).join(',');
    // texts shown on the map markers (people today · people per minute)
    const ov: Record<string, string> = useMemo(() => {
        const t: Record<string, string> = {
            temple: k.inside != null ? `Inside now: ${n(k.inside)}` : 'Inside: no camera',
            rail: nodeText('RAIL'), bus: nodeText('BUS'), road: 'No camera',
            jGEDDA: nodeText('GEDDA'), jVINAYAKA: nodeText('VINAYAKA'),
        };
        junctionNodes.forEach(j => { t[`j${j.id}`] = nodeText(j.id); });
        // Junction Sequential Flow & Transit Monitoring markers: people today · per minute, and the transit into the node
        (api?.junction_flow?.nodes ?? []).forEach((j: any) => {
            const r = j.per_min == null ? '—' : Math.round(j.per_min);   // whole people per minute, no decimals
            t[`jf${j.id}`] = j.today != null ? `${n(j.today)} · ${r}/min` : (JF_STATE_TEXT[j.state] ?? 'No camera');
            t[`jfs${j.id}`] = j.transit ? (JF_TRANSIT[j.transit.status]?.text ?? 'No data') +
                (j.transit.ratio != null ? ` ${Math.round(j.transit.ratio * 100)}%` : '') : (JF_STATE_TEXT[j.state] ?? '');
        });
        return t;
    }, [api]);   // eslint-disable-line react-hooks/exhaustive-deps
    const jfNodes: any[] = (api?.junction_flow?.nodes ?? []).filter((x: any) => x.lat != null && x.lng != null);
    const jfKey = jfNodes.map(j => j.id).join(',');
    const jfRef = useRef<{ nodes: any[]; links: any[] }>({ nodes: [], links: [] });
    jfRef.current = { nodes: jfNodes, links: api?.junction_flow?.links ?? [] };
    const jfLinesRef = useRef<Record<string, any>>({});
    const ovRef = useRef(ov);
    ovRef.current = ov;
    const junctionsRef = useRef(junctionNodes);
    junctionsRef.current = junctionNodes;

    const zones: QueueZoneData[] = (api?.zones ?? []).map((z: any, zi: number) => {
        const pct = z.count != null && z.cap ? Math.round((z.count / z.cap) * 100) : null;
        return {
            // the zone names of the server carry the camera addresses, "Tollgate (192.168.2.138) → Ghat Turning (192.168.2.112)":
            // the cards show the place names only
            id: z.id, label: zoneLabel(zi), name: noAddress(z.name), count: z.count ?? null, density: z.density ?? null,
            inflow: z.inRate ?? null, outflow: z.outRate ?? null, dwellTime: z.dwell ?? null,
            // minutes to clear the zone at the present outflow; only valid when the server has a real entry-exit
            // balance. A visible-floor count is a measured lower bound, not whole-zone occupancy.
            entryNow: z.inNow ?? null, exitNow: z.outNow ?? null,
            // the server's own wait when it sends one (overview.py "eta_adj"; was hand-patched into the navaratri build
            // at 11:38 on 15 Oct 2026, now in the source), else count / outflow
            estWait: typeof z.eta_adj === 'number' ? z.eta_adj
                : z.method !== 'entry-exit' || z.count == null || z.data_ok !== true || z.count_mode !== 'entry-exit' ? null
                    : z.count === 0 ? 0
                        : !z.outRate || z.outRate < 0.2 ? Infinity
                            : Math.max(1, Math.round(z.count / z.outRate)),
            passed: z.passed ?? null, hasCam: z.source === 'camera',
            statusColor: pct == null ? 'teal' : pct >= 90 ? 'rose' : pct >= 70 ? 'amber' : 'emerald',
            entryCams: z.entry_cameras ?? [], exitCams: z.exit_cameras ?? [],
        };
    });
    const holdAreas: any[] = api?.holding ?? [];
    // entry / exit cameras of each holding area and what they counted (GET /api/holding, every 15 s; 15 Oct 2026)
    const [holdIo, setHoldIo] = useState<Record<string, any>>({});
    useEffect(() => {
        let alive = true;
        const load = () => baseapi.get('holding').then(r => {
            if (!alive) return;
            const m: Record<string, any> = {};
            for (const a of r.data?.areas || []) m[a.id] = a;
            setHoldIo(m);
        }).catch(() => { /* keep the last */ });
        load();
        const t = window.setInterval(() => { if (!document.hidden) load(); }, 15000);
        return () => { alive = false; window.clearInterval(t); };
    }, []);
    const holdAreasTotal: number | null = holdAreas.length > 0 && holdAreas.some((h: any) => typeof h.count === 'number')
        ? holdAreas.reduce((sum: number, h: any) => sum + (typeof h.count === 'number' ? h.count : 0), 0)
        : null;
    const holdingCount: number | null = k.holding ?? holdAreasTotal;
    const boxLabel = (id: string) => id.replace(/^HA(\d)-B/, 'H$1-');

    // Google Maps references
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const trafficLayerRef = useRef<any>(null);
    const customOverlaysRef = useRef<any[]>([]);
    const polylinesRef = useRef<any[]>([]);

    // The Google map markers are plain HTML built once per map: refresh their figures in place.
    useEffect(() => {
        const apply = () => Object.entries(ov).forEach(([key, val]) =>
            document.querySelectorAll(`[data-ov="${key}"]`).forEach(el => { el.textContent = val; }));
        apply();
        const t = window.setTimeout(apply, 1500);     // markers are attached a moment after the map is created
        return () => window.clearTimeout(t);
    }, [ov, mapMode]);

    // Clean up all overlays and polylines on map
    const clearMapElements = () => {
        customOverlaysRef.current.forEach(ov => {
            if (ov && typeof ov.setMap === 'function') ov.setMap(null);
        });
        customOverlaysRef.current = [];

        polylinesRef.current.forEach(p => {
            if (p && typeof p.setMap === 'function') p.setMap(null);
        });
        polylinesRef.current = [];
    };

    // Google Maps Initialization Callback
    const initGoogleMap = useCallback(() => {
        if (!mapContainerRef.current || !window.google?.maps?.Map) return;

        try {
            const mapOptions = {
                center: { lat: 16.5132, lng: 80.6220 },
                zoom: 12.6,
                mapTypeId: mapMode === 'satellite' ? 'hybrid' : 'roadmap',
                styles: mapMode === 'satellite' ? [] : (isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT),
                disableDefaultUI: true,
                zoomControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            };

            const map = new window.google.maps.Map(mapContainerRef.current, mapOptions);
            mapInstanceRef.current = map;

            // Traffic Layer (OFF by default to keep normal roads and pointed roads clearly distinguishable)
            const traffic = new window.google.maps.TrafficLayer();
            if (showTraffic) {
                traffic.setMap(map);
            }
            trafficLayerRef.current = traffic;

            clearMapElements();

            // --- 1. RENDER POINTED INBOUND FLOW ROADS (HYDERABAD, GUNTUR, ELURU) ---
            const inboundRoutes = [
                { name: 'NH-65 Hyderabad Inflow', path: HYD_ROAD_POINTS, color: '#10b981' },
                { name: 'NH-16 Guntur Inflow', path: GNT_ROAD_POINTS, color: '#10b981' },
                { name: 'NH-16 Eluru Inflow', path: ELR_ROAD_POINTS, color: '#10b981' },
            ];

            inboundRoutes.forEach(r => {
                // Casing Line (Thick black boundary to make road pop out)
                const casing = new window.google.maps.Polyline({
                    path: r.path,
                    strokeColor: isDark ? '#000000' : '#0f172a',
                    strokeOpacity: 0.95,
                    strokeWeight: 8,
                    map: map,
                    zIndex: 5
                });

                // Vibrant Flow Line with Repeating Pointed Chevron Arrows
                const flowLine = new window.google.maps.Polyline({
                    path: r.path,
                    strokeColor: r.color,
                    strokeOpacity: 1,
                    strokeWeight: 4.5,
                    map: map,
                    zIndex: 6,
                    icons: [
                        {
                            icon: {
                                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 3,
                                strokeColor: '#000000',
                                strokeWeight: 1,
                                fillColor: '#ffffff',
                                fillOpacity: 1
                            },
                            offset: '20px',
                            repeat: '45px'
                        }
                    ]
                });

                polylinesRef.current.push(casing, flowLine);
            });

            // --- 2. RENDER DISTINCT RAILWAY LINE (DASHED TRACK) ---
            const railCasing = new window.google.maps.Polyline({
                path: RAILWAY_LINE_POINTS,
                strokeColor: '#334155',
                strokeOpacity: 0.8,
                strokeWeight: 4,
                map: map,
                zIndex: 3
            });

            const railTrack = new window.google.maps.Polyline({
                path: RAILWAY_LINE_POINTS,
                strokeColor: '#f8fafc',
                strokeOpacity: 1,
                strokeWeight: 2,
                map: map,
                zIndex: 4,
                icons: [
                    {
                        icon: {
                            path: 'M 0,-1 0,1',
                            strokeColor: '#f8fafc',
                            strokeOpacity: 1,
                            strokeWeight: 2.5,
                            scale: 3
                        },
                        offset: '0',
                        repeat: '10px'
                    }
                ]
            });

            polylinesRef.current.push(railCasing, railTrack);

            // --- 3. CUSTOM RICH HTML OVERLAY CLASS FOR CALLOUT BADGES & SHRINE ---
            class CustomHtmlOverlay extends window.google.maps.OverlayView {
                position: any;
                element: HTMLElement;
                div: HTMLDivElement | null = null;

                constructor(pos: { lat: number; lng: number }, elem: HTMLElement) {
                    super();
                    this.position = new window.google.maps.LatLng(pos.lat, pos.lng);
                    this.element = elem;
                }

                onAdd() {
                    this.div = document.createElement('div');
                    this.div.style.position = 'absolute';
                    this.div.style.cursor = 'pointer';
                    this.div.style.zIndex = '50';
                    this.div.appendChild(this.element);
                    const panes = this.getPanes();
                    panes.overlayMouseTarget.appendChild(this.div);
                }

                draw() {
                    const overlayProjection = this.getProjection();
                    if (!overlayProjection || !this.div) return;
                    const point = overlayProjection.fromLatLngToDivPixel(this.position);
                    if (point) {
                        this.div.style.left = point.x + 'px';
                        this.div.style.top = point.y + 'px';
                        this.div.style.transform = 'translate(-50%, -50%)';
                    }
                }

                onRemove() {
                    if (this.div && this.div.parentNode) {
                        this.div.parentNode.removeChild(this.div);
                        this.div = null;
                    }
                }
            }

            // Helper to create HTML badge overlay
            const addCustomHtmlMarker = (pos: { lat: number; lng: number }, htmlContent: string, onClick?: () => void) => {
                const container = document.createElement('div');
                container.innerHTML = htmlContent;
                if (onClick) {
                    container.onclick = (e) => {
                        e.stopPropagation();
                        onClick();
                    };
                }
                const overlay = new CustomHtmlOverlay(pos, container);
                overlay.setMap(map);
                customOverlaysRef.current.push(overlay);
            };

            // 1. KANAKA DURGA TEMPLE LANDMARK (PULSING GOLD SHRINE)
            addCustomHtmlMarker(
                TEMPLE_COORD,
                `
          <div style="position:relative;display:flex;align-items:center;filter:drop-shadow(0 4px 12px rgba(0,0,0,0.4));">
            <!-- Pulsing Radar Glow -->
            <div style="position:absolute;left:-8px;top:-8px;width:38px;height:38px;border-radius:50%;background:rgba(239,68,68,0.3);animation:ping 2s cubic-bezier(0,0,0.2,1) infinite;"></div>
            <div style="display:flex;align-items:center;gap:6px;padding:4px 8px;border-radius:8px;background:#18181b;border:1.5px solid #f97316;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;font-size:11px;font-weight:800;white-space:nowrap;box-shadow:0 2px 10px rgba(249,115,22,0.4);">
              <span style="font-size:16px;">🛕</span>
              <div style="display:flex;flex-direction:column;line-height:1.15;">
                <span style="color:#fb923c;font-size:11px;font-weight:900;">Kanaka Durga Temple</span>
                <span data-ov="temple" style="color:#fca5a5;font-size:9.5px;font-weight:700;">${ovRef.current.temple}</span>
              </div>
            </div>
          </div>
        `,
                () => setSelectedCityNode(`Kanaka Durga Temple (${ovRef.current.temple})`)
            );

            const hasJf = jfRef.current.nodes.length > 0;
            // 2. RAILWAY STATION BADGE (WITH TRAIN ICON) - replaced by the junction layer (stage 1 gates) when present
            if (!hasJf) addCustomHtmlMarker(
                RAILWAY_STN_COORD,
                `
          <div style="display:flex;align-items:center;gap:5px;padding:3.5px 7px;border-radius:6px;background:#0f172a;border:1.5px solid #38bdf8;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;font-size:10px;font-weight:800;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.5);">
            <span style="font-size:13px;">🚆</span>
            <div style="display:flex;flex-direction:column;line-height:1.15;">
              <span style="color:#f1f5f9;font-weight:800;font-size:10px;">Railway Station</span>
              <span data-ov="rail" style="color:#38bdf8;font-weight:700;font-size:9px;">${ovRef.current.rail}</span>
            </div>
          </div>
        `,
                () => setSelectedCityNode(`Vijayawada Railway Station (${ovRef.current.rail})`)
            );

            // 3. PNBS BUS STATION BADGE (WITH BUS ICON) - replaced by the junction layer when present
            if (!hasJf) addCustomHtmlMarker(
                PNBS_BUS_COORD,
                `
          <div style="display:flex;align-items:center;gap:5px;padding:3.5px 7px;border-radius:6px;background:#0f172a;border:1.5px solid #38bdf8;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;font-size:10px;font-weight:800;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.5);">
            <span style="font-size:13px;">🚌</span>
            <div style="display:flex;flex-direction:column;line-height:1.15;">
              <span style="color:#f1f5f9;font-weight:800;font-size:10px;">PNBS Bus Station</span>
              <span data-ov="bus" style="color:#38bdf8;font-weight:700;font-size:9px;">${ovRef.current.bus}</span>
            </div>
          </div>
        `,
                () => setSelectedCityNode(`PNBS Bus Station (${ovRef.current.bus})`)
            );

            // 4. FROM HYDERABAD (NH-65) ENTRY CALLOUT
            addCustomHtmlMarker(
                HYD_ENTRY_COORD,
                `
          <div style="display:flex;flex-direction:column;padding:4px 8px;border-radius:6px;background:#0f172a;border:1.5px solid #10b981;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 3px 10px rgba(16,185,129,0.3);white-space:nowrap;">
            <div style="color:#94a3b8;font-size:9px;font-weight:700;display:flex;align-items:center;gap:3px;">
              <span>➔</span> From Hyderabad (NH-65)
            </div>
            <div style="font-size:11.5px;font-weight:900;color:#34d399;line-height:1.1;">
              <span data-ov="road" style="font-size:10px;color:#94a3b8;font-weight:700;">${ovRef.current.road}</span>
            </div>
          </div>
        `,
                () => setSelectedCityNode('From Hyderabad (NH-65) – no camera on this road yet')
            );

            // 5. FROM GUNTUR (NH-16) ENTRY CALLOUT
            addCustomHtmlMarker(
                GNT_ENTRY_COORD,
                `
          <div style="display:flex;flex-direction:column;padding:4px 8px;border-radius:6px;background:#0f172a;border:1.5px solid #10b981;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 3px 10px rgba(16,185,129,0.3);white-space:nowrap;">
            <div style="color:#94a3b8;font-size:9px;font-weight:700;display:flex;align-items:center;gap:3px;">
              <span>➔</span> From Guntur (NH-16)
            </div>
            <div style="font-size:11.5px;font-weight:900;color:#34d399;line-height:1.1;">
              <span data-ov="road" style="font-size:10px;color:#94a3b8;font-weight:700;">${ovRef.current.road}</span>
            </div>
          </div>
        `,
                () => setSelectedCityNode('From Guntur (NH-16) – no camera on this road yet')
            );

            // 6. FROM ELURU (NH-16) ENTRY CALLOUT
            addCustomHtmlMarker(
                ELR_ENTRY_COORD,
                `
          <div style="display:flex;flex-direction:column;padding:4px 8px;border-radius:6px;background:#0f172a;border:1.5px solid #10b981;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 3px 10px rgba(16,185,129,0.3);white-space:nowrap;">
            <div style="color:#94a3b8;font-size:9px;font-weight:700;display:flex;align-items:center;gap:3px;">
              <span>➔</span> From Eluru (NH-16)
            </div>
            <div style="font-size:11.5px;font-weight:900;color:#34d399;line-height:1.1;">
              <span data-ov="road" style="font-size:10px;color:#94a3b8;font-weight:700;">${ovRef.current.road}</span>
            </div>
          </div>
        `,
                () => setSelectedCityNode('From Eluru (NH-16) – no camera on this road yet')
            );

            // 7. JUNCTIONS (AUTO NAGAR, GOVERNORPET, CURRENCY NAGAR)
            // only junctions that have a counting camera (their position comes from the camera itself)
            const junctions = junctionsRef.current.map((j: any) => ({
                name: j.name, key: `j${j.id}`, count: ovRef.current[`j${j.id}`] ?? 'No camera', pos: { lat: j.lat, lng: j.lng },
            }));

            (hasJf ? [] : junctions).forEach(j => {
                addCustomHtmlMarker(
                    j.pos,
                    `
            <div style="display:flex;align-items:center;gap:4px;padding:3px 6px;border-radius:5px;background:#0f172a;border:1.5px solid #2dd4bf;color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.4);">
              <span style="width:6px;height:6px;border-radius:50%;background:#2dd4bf;"></span>
              <div style="display:flex;flex-direction:column;line-height:1.1;">
                <span style="color:#f8fafc;font-size:9px;font-weight:800;">${j.name}</span>
                <span data-ov="${j.key}" style="color:#2dd4bf;font-size:8.5px;font-weight:700;">${j.count}</span>
              </div>
            </div>
          `,
                    () => setSelectedCityNode(`${j.name} (${ovRef.current[j.key] ?? j.count})`)
                );
            });

            // 8. JUNCTION SEQUENTIAL FLOW & TRANSIT MONITORING (api.junction_flow): arrows between the junctions in
            // sequence, coloured by the transit into the next one, and a badge per junction (stage no., people
            // today · per minute, transit). Figures and colours are refreshed in place (see the effect below).
            jfLinesRef.current = {};
            const jfPos: Record<string, { lat: number; lng: number }> = {};
            jfRef.current.nodes.forEach((j: any) => { jfPos[j.id] = { lat: j.lat, lng: j.lng }; });
            jfRef.current.links.forEach((l: any) => {
                const a = jfPos[l.from], b = jfPos[l.to];
                if (!a || !b) return;
                const color = (JF_TRANSIT[l.status] ?? JF_TRANSIT.no_data).color;
                const line = new window.google.maps.Polyline({
                    path: [a, b], geodesic: false, strokeColor: color, strokeOpacity: 0.95, strokeWeight: 3.5, zIndex: 40, map,
                    icons: [{
                        icon: {
                            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.6, strokeColor: color,
                            fillColor: color, fillOpacity: 1
                        }, offset: '55%'
                    }],
                });
                polylinesRef.current.push(line);
                jfLinesRef.current[`${l.from}>${l.to}`] = line;
            });
            jfRef.current.nodes.forEach((j: any) => {
                const accent = JF_KIND_COLOR[j.kind] ?? '#94a3b8';
                // the temple badge sits just below the temple landmark so the two do not cover each other
                const pos = j.id === 'TEMPLE' ? { lat: j.lat - 0.0013, lng: j.lng } : { lat: j.lat, lng: j.lng };
                addCustomHtmlMarker(
                    pos,
                    `
            <div style="display:flex;align-items:center;gap:4px;padding:3px 6px;border-radius:5px;background:#0f172a;border:1.5px solid ${accent};color:#ffffff;font-family:'Plus Jakarta Sans',sans-serif;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.45);">
              <span style="min-width:15px;height:15px;border-radius:50%;background:${accent};color:#0f172a;font-size:9px;font-weight:900;display:flex;align-items:center;justify-content:center;">${j.stage_no ?? ''}</span>
              <div style="display:flex;flex-direction:column;line-height:1.12;">
                <span style="color:#f8fafc;font-size:9px;font-weight:800;"><span data-jfdot="${j.id}" style="display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:3px;background:${JF_STATE_DOT[j.state] ?? '#94a3b8'};"></span>${j.name}${j.position_approx ? ' ≈' : ''}</span>
                <span data-ov="jf${j.id}" style="color:#e2e8f0;font-size:8.5px;font-weight:700;">${ovRef.current[`jf${j.id}`] ?? ''}</span>
                <span data-ov="jfs${j.id}" style="color:#94a3b8;font-size:8px;font-weight:700;">${ovRef.current[`jfs${j.id}`] ?? ''}</span>
              </div>
            </div>
          `,
                    () => setSelectedCityNode(`${j.stage_no ?? ''}. ${j.name} (${ovRef.current[`jf${j.id}`] ?? ''} · ${ovRef.current[`jfs${j.id}`] ?? ''})`)
                );
            });

            // Dynamically fit all incoming highway corridors, transit hubs, and temple
            const fitAllCityBounds = () => {
                if (!map || !window.google?.maps) return;
                const bounds = new window.google.maps.LatLngBounds();
                [HYD_ENTRY_COORD, GNT_ENTRY_COORD, ELR_ENTRY_COORD, TEMPLE_COORD, RAILWAY_STN_COORD, PNBS_BUS_COORD].forEach(pt => bounds.extend(pt));
                HYD_ROAD_POINTS.forEach(pt => bounds.extend(pt));
                GNT_ROAD_POINTS.forEach(pt => bounds.extend(pt));
                ELR_ROAD_POINTS.forEach(pt => bounds.extend(pt));
                RAILWAY_LINE_POINTS.forEach(pt => bounds.extend(pt));
                junctionsRef.current.forEach(j => {
                    if (j.lat != null && j.lng != null) bounds.extend({ lat: j.lat, lng: j.lng });
                });
                jfRef.current.nodes.forEach((j: any) => bounds.extend({ lat: j.lat, lng: j.lng }));
                map.fitBounds(bounds, { top: 25, bottom: 25, left: 25, right: 25 });
            };

            fitAllCityBounds();
            window.google.maps.event.addListenerOnce(map, 'idle', fitAllCityBounds);

        } catch (e) {
            console.warn('Google Maps initialization warning:', e);
        }
    }, [mapMode, isDark, showTraffic, junctionKey, jfKey]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Junction layer: arrow colours (transit into the next junction) and camera dots follow every new answer
    useEffect(() => {
        jfRef.current.links.forEach((l: any) => {
            const line = jfLinesRef.current[`${l.from}>${l.to}`];
            if (!line) return;
            const color = (JF_TRANSIT[l.status] ?? JF_TRANSIT.no_data).color;
            line.setOptions({
                strokeColor: color, icons: [{
                    icon: {
                        path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW, scale: 2.6,
                        strokeColor: color, fillColor: color, fillOpacity: 1
                    }, offset: '55%'
                }]
            });
        });
        jfRef.current.nodes.forEach((j: any) => document.querySelectorAll(`[data-jfdot="${j.id}"]`).forEach(el => {
            (el as HTMLElement).style.background = JF_STATE_DOT[j.state] ?? '#94a3b8';
        }));
    }, [api]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Load Google Maps Script
    useEffect(() => {
        if (mapMode === 'tactical') return;

        if (window.google?.maps?.Map) {
            initGoogleMap();
            return;
        }

        const scriptId = 'google-maps-overview-script';
        let script = document.getElementById(scriptId) as HTMLScriptElement;

        if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${DEFAULT_MAPS_KEY}&libraries=geometry`;
            script.async = true;
            script.defer = true;
            window.__overviewGmapsReady = () => {
                initGoogleMap();
            };
            script.onload = () => {
                if (window.google?.maps) initGoogleMap();
            };
            document.head.appendChild(script);
        } else {
            script.onload = () => {
                if (window.google?.maps) initGoogleMap();
            };
        }
    }, [mapMode, initGoogleMap]);

    // Update theme styling on Google Maps instance
    useEffect(() => {
        if (mapInstanceRef.current && window.google?.maps) {
            if (mapMode === 'satellite') {
                mapInstanceRef.current.setMapTypeId('hybrid');
                mapInstanceRef.current.setOptions({ styles: [] });
            } else {
                mapInstanceRef.current.setMapTypeId('roadmap');
                mapInstanceRef.current.setOptions({
                    styles: isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT
                });
            }
        }
    }, [isDark, mapMode]);

    // Handle container resize dynamically so map always fills container smoothly
    useEffect(() => {
        if (!mapContainerRef.current || !window.google?.maps) return;
        const resizeObserver = new ResizeObserver(() => {
            if (mapInstanceRef.current && window.google?.maps?.event) {
                window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
            }
        });
        resizeObserver.observe(mapContainerRef.current);
        return () => resizeObserver.disconnect();
    }, [mapMode]);

    // Toggle Traffic Layer
    const toggleTraffic = () => {
        if (trafficLayerRef.current && mapInstanceRef.current) {
            if (showTraffic) {
                trafficLayerRef.current.setMap(null);
                setShowTraffic(false);
            } else {
                trafficLayerRef.current.setMap(mapInstanceRef.current);
                setShowTraffic(true);
            }
        }
    };

    const handleZoom = (delta: number) => {
        if (mapInstanceRef.current) {
            const currentZoom = mapInstanceRef.current.getZoom() || 13;
            mapInstanceRef.current.setZoom(currentZoom + delta);
        }
    };

    const handleRecenter = () => {
        if (mapInstanceRef.current && window.google?.maps) {
            const bounds = new window.google.maps.LatLngBounds();
            [HYD_ENTRY_COORD, GNT_ENTRY_COORD, ELR_ENTRY_COORD, TEMPLE_COORD, RAILWAY_STN_COORD, PNBS_BUS_COORD].forEach(pt => bounds.extend(pt));
            HYD_ROAD_POINTS.forEach(pt => bounds.extend(pt));
            GNT_ROAD_POINTS.forEach(pt => bounds.extend(pt));
            ELR_ROAD_POINTS.forEach(pt => bounds.extend(pt));
            RAILWAY_LINE_POINTS.forEach(pt => bounds.extend(pt));
            junctionsRef.current.forEach(j => {
                if (j.lat != null && j.lng != null) bounds.extend({ lat: j.lat, lng: j.lng });
            });
            mapInstanceRef.current.fitBounds(bounds, { top: 25, bottom: 25, left: 25, right: 25 });
        }
    };
    void { toggleTraffic, handleZoom, handleRecenter };

    return (
        <div className="space-y-1 sm:space-y-1.5 mx-auto pb-1">
            {/* 1. 6 PRIMARY KPI SUMMARY CARDS: hidden by default, opened with this button */}
            <div className="flex items-center">
                <button
                    type="button"
                    onClick={() => setShowSummary(v => !v)}
                    aria-expanded={showSummary}
                    title={showSummary ? 'Hide the summary cards' : 'Show the summary cards: darshan completed, holding areas, waiting time, Antharalayam, busiest hour'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] text-sm font-black text-neutral-700 dark:text-neutral-200 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 shadow-xs transition-colors cursor-pointer"
                >
                    <span>Summary</span>
                    <ChevronDown className={`w-4 h-4 stroke-[2.5] transition-transform ${showSummary ? 'rotate-180' : ''}`} />
                </button>
            </div>
            {showSummary && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 sm:gap-1.5">
                    {/* 1. Darshan completed today */}
                    <button type="button" onClick={() => setShowDarshanCompleted(true)} aria-label="Open Darshan Completed minute-by-minute comparison" className="text-left p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs hover:border-[#761D20]/50 dark:hover:border-blue-500/60 transition-colors flex flex-col justify-between cursor-pointer focus-visible:outline-2 focus-visible:outline-blue-500">
                        <div className="flex items-start justify-between text-[11px] sm:text-xs font-black text-neutral-800 dark:text-neutral-100 uppercase tracking-tight gap-1">
                            <span className="text-[11px] sm:text-xs tracking-tight leading-tight" title="Devotees who completed darshan today">Total Devotees Darshan Completed</span>
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-[#761D20] dark:text-emerald-500 stroke-[2.5] mt-0.5" />
                        </div>
                        <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-100 tracking-tight leading-none">
                            {n(k.darshan)}
                        </div>
                        <div className="mt-1 text-[11px] sm:text-xs text-[#C2620F] dark:text-emerald-400 font-bold flex items-center gap-0.5 leading-snug">
                            Devotees who completed darshan
                        </div>
                    </button>

                    {/* 2. Holding Areas */}
                    <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs hover:border-[#761D20]/30 dark:hover:border-emerald-500/40 transition-colors flex flex-col justify-between">
                        <div className="flex items-start justify-between text-[11px] sm:text-xs font-black text-neutral-800 dark:text-neutral-100 uppercase tracking-tight gap-1">
                            <span className="text-[11px] sm:text-xs tracking-tight leading-tight" title="Devotees waiting in the holding areas now">Devotees in Holding Areas</span>
                            <Warehouse className="w-3.5 h-3.5 shrink-0 text-[#761D20] dark:text-emerald-500 stroke-[2.5] mt-0.5" />
                        </div>
                        <div className="mt-1 text-2xl sm:text-[28px] font-black text-emerald-600 dark:text-emerald-400 tracking-tight leading-none">
                            {n(holdingCount)}
                        </div>
                        <div className="mt-1 text-[11px] sm:text-xs text-neutral-700 dark:text-neutral-200 font-bold leading-snug"
                            title={holdAreas.filter((h: any) => h.count != null).map((h: any) => `${h.name || h.id}: ${n(h.count)}`).join(' · ')}>
                            {holdingCount != null ? (
                                holdAreas.length > 0 && holdAreas.some((h: any) => h.count != null) ? (
                                    <span>
                                        Now · {holdAreas.filter((h: any) => h.count != null).map((h: any, idx: number, arr: any[]) => {
                                            const colors = [
                                                'text-[#BE123C] dark:text-rose-400',
                                                'text-[#C2620F] dark:text-amber-400',
                                                'text-emerald-700 dark:text-emerald-400',
                                            ];
                                            const countColor = colors[idx % colors.length];
                                            return (
                                                <React.Fragment key={h.id}>
                                                    <span>{h.id}: <strong className={`font-black ${countColor}`}>{n(h.count)}</strong></span>
                                                    {idx < arr.length - 1 && <span className="mx-0.5 text-neutral-400 dark:text-neutral-500">·</span>}
                                                </React.Fragment>
                                            );
                                        })}
                                    </span>
                                ) : (
                                    'Now · HA-1, HA-2, HA-3'
                                )
                            ) : (
                                'No box cameras yet'
                            )}
                        </div>
                    </div>

                    {/* Average the current Darshan Evidence estimates for both queues */}
                    <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs hover:border-[#C99A1E]/50 dark:hover:border-violet-500/40 transition-colors flex flex-col justify-between">
                        <div className="flex items-start justify-between text-[11px] sm:text-xs font-black text-neutral-800 dark:text-neutral-100 uppercase tracking-tight gap-1">
                            <span className="text-[11px] sm:text-xs tracking-tight leading-tight" title="How long a devotee joining now will take to reach darshan">Avg Estimated Darshan Time</span>
                            <Clock className="w-3.5 h-3.5 shrink-0 text-[#C99A1E] dark:text-violet-500 stroke-[2.5] mt-0.5" />
                        </div>
                        <div className="mt-1 text-2xl sm:text-[28px] font-black text-[#8A6508] dark:text-violet-400 tracking-tight leading-none">
                            {fmtMin(displayDarshanMin)}
                        </div>
                    </div>

                    {/* 4. Antharalayam: people inside the sanctum */}
                    <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs hover:border-[#E6781B]/40 dark:hover:border-amber-500/40 transition-colors flex flex-col justify-between">
                        <div className="flex items-start justify-between text-[11px] sm:text-xs font-black text-neutral-800 dark:text-neutral-100 uppercase tracking-tight gap-1">
                            <span className="text-[11px] sm:text-xs tracking-tight leading-tight" title="Devotees inside the Antharalayam now">Devotees Inside Antharalayam</span>
                            <Landmark className="w-3.5 h-3.5 shrink-0 text-[#E6781B] dark:text-amber-500 stroke-[2.5] mt-0.5" />
                        </div>
                        <div className="mt-1 text-2xl sm:text-[28px] font-black text-[#C2620F] dark:text-amber-400 tracking-tight leading-none">
                            {n(k.antharalayam)}
                        </div>
                        <div className={`mt-1 text-[11px] sm:text-xs font-bold flex items-center gap-0.5 leading-snug ${(k.antharalayam_out_60m ?? k.antharalayam_out_15m) != null ? 'text-[#BE123C] dark:text-blue-400' : 'text-neutral-700 dark:text-neutral-200'}`}
                            title={k.antharalayam_in != null ? `Inside now = entered ${n(k.antharalayam_in)} − exited ${n(k.antharalayam_out)} today` : undefined}>
                            {k.antharalayam_out_60m != null
                                ? <><TrendingDown className="w-3 h-3 stroke-[2.5]" /> {n(k.antharalayam_out_60m)} exited in last 1 hour</>
                                : k.antharalayam_out_15m != null
                                    ? <><TrendingDown className="w-3 h-3 stroke-[2.5]" /> {n(k.antharalayam_out_15m)} exited out in last 15 min</>
                                    : 'Inside now · entered − exited'}
                        </div>
                    </div>

                    {/* 5. Peak Hour */}
                    <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs hover:border-orange-500/40 transition-colors flex flex-col justify-between">
                        <div className="flex items-start justify-between text-[11px] sm:text-xs font-black text-neutral-800 dark:text-neutral-100 uppercase tracking-tight gap-1">
                            <span className="flex items-center gap-1 text-[11px] sm:text-xs tracking-tight leading-tight text-orange-600 dark:text-orange-400" title="Peak hour – most devotees entered">
                                <Clock className="w-3.5 h-3.5 shrink-0 stroke-[2.5]" />
                                Busiest Hour
                            </span>
                            <span className="text-[9.5px] font-bold px-1.5 py-0.2 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5">
                                Today
                            </span>
                        </div>
                        <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-100 tracking-tight leading-none">
                            {devoteeMetrics?.peakHour ? `${hm(devoteeMetrics.peakHour[0])}–${hm(devoteeMetrics.peakHour[0] + 3600)}` : '—'}
                        </div>
                        <div className="mt-1 text-[11px] sm:text-xs text-neutral-700 dark:text-neutral-200 font-bold leading-snug">
                            {devoteeMetrics?.peakHour ? (
                                <>
                                    <strong className="text-neutral-900 dark:text-neutral-100 font-black">{n(devoteeMetrics.peakHour[1])}</strong> entered · Avg <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{rate(devoteeMetrics.peakHour[1] / 60)}</strong>/min
                                </>
                            ) : (
                                'Waiting for data'
                            )}
                        </div>
                    </div>

                </div>
            )}

            {/* 1b. Devotees for Darshan - peak trends (per-minute door counts, window Today / 15m / 30m / 60m / 120m) */}
            <DevoteeTrend hideCards onMetricsChange={setDevoteeMetrics} />

            {/* 2. MAIN SECTION: CITY LIVE CROWD MOVEMENT MAP (LEFT) & TEMPLE PILGRIM JOURNEY FLOW (RIGHT) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-1 sm:gap-1.5">
                {/* Pilgrim Journey Flow & Operations Intelligence (the city junction-flow map was removed 14 Oct 2026: no longer needed) */}
                <div className="lg:col-span-12 flex flex-col gap-1 sm:gap-1.5">
                    {/* the "Pilgrim journey flow" card was removed 15 Oct 2026: the holding areas, queue zones and the
                        darshan in / out are all shown below and above in more detail; the card repeated them */}

                    {/* Temple Throughput Chart & Operations Intelligence */}
                    <div className="flex flex-col gap-1 sm:gap-1.5">
                        {/* 1. Navaratri so far (chart & key stats) */}
                        <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs">
                            <FestivalTrend tableTarget={festTableSlot} />
                        </div>

                        {/* 2. Navaratri days compared (comparison table below) */}
                        <div className="p-2 sm:p-2.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs flex flex-col">
                            <div ref={setFestTableSlot} className="flex-1 flex flex-col min-h-0" />
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. QUEUE ZONES – LIVE STATUS (CITY ➔ TEMPLE) */}
            {/* 3. QUEUE LINES (CITY -> TEMPLE CORRIDOR) */}
            <div className="rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs p-2 sm:p-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-neutral-100 dark:border-[#262b37]">
                    <div>
                        <h2 className="text-base sm:text-lg font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                            <span className="p-1 rounded-md bg-[#761D20]/10 text-[#761D20] dark:bg-white/10 dark:text-neutral-100">
                                <Layers className="w-4 h-4 stroke-[2.5]" />
                            </span>
                            <span>Queue lines – live (city ➔ temple)</span>
                        </h2>
                        <p className="text-xs sm:text-[13px] font-bold text-neutral-600 dark:text-neutral-300 leading-normal mt-0.5">
                            How many people are in each queue now, how fast they move, and how long the wait is
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-bold">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-black text-[11px] border border-emerald-500/20">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Live · updated {overviewUpdatedText}
                        </div>
                        <div className="text-neutral-600 dark:text-neutral-300">
                            {zones.filter(z => z.hasCam).length} of {zones.length} queues have cameras
                        </div>
                    </div>
                </div>

                {/* 5 Queue Zone Cards in Pipeline sequence */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-1.5 sm:gap-2 mt-2">
                    {zones.map((zone) => {
                        const live = zone.hasCam && zone.count != null;
                        const w = zone.estWait;
                        const waitText = w == null ? '—' : w === 0 ? 'No wait' : w === Infinity ? 'Not moving' : w >= 180 ? '3 h +' : w >= 60 ? `${Math.floor(w / 60)} h ${w % 60} min` : `${w} min`;
                        const waitBadge = w == null
                            ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700'
                            : w === Infinity || w >= 45
                                ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30 font-black'
                                : w >= 15
                                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 font-black'
                                    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 font-black';

                        const parts = zone.name.split(/\s*→\s*/);
                        const origin = parts[0] || zone.name;
                        const destination = parts.slice(1).join(' → ');

                        return (
                            <div
                                key={zone.id}
                                className={`rounded-xl border p-2.5 sm:p-3 flex flex-col justify-between transition-all ${live
                                    ? 'bg-white dark:bg-[#151720] border-neutral-200/90 dark:border-[#262b37] shadow-xs hover:border-[#761D20]/40 dark:hover:border-neutral-600 hover:shadow-sm'
                                    : 'bg-neutral-50/70 dark:bg-[#14161e] border-dashed border-neutral-300 dark:border-[#2b3140] opacity-80'
                                    }`}
                                title={`${zone.label} · ${zone.name}`}
                            >
                                <div>
                                    {/* Card Header: Zone Label & Wait Badge in Top Right */}
                                    <div className="flex items-center justify-between gap-1.5 pb-2 border-b border-neutral-100 dark:border-[#222735]">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`px-2 py-0.5 rounded-md text-[13px] sm:text-sm font-black ${live ? 'bg-[#761D20] text-white dark:bg-white/25' : 'bg-neutral-300/70 text-neutral-600 dark:bg-white/10 dark:text-neutral-300'}`}>
                                                {zone.statusColor === 'rose' && <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping mr-1" />}
                                                {zone.label}
                                            </span>
                                        </div>
                                        <div
                                            className={`px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 border shadow-2xs ${waitBadge}`}
                                            title={w == null ? 'Wait estimate unavailable' : w === Infinity ? 'People are in the zone but almost nobody is leaving it' : 'People in the zone ÷ people leaving per minute (last 15 min)'}
                                        >
                                            <Clock className="w-3 h-3 stroke-[2.2]" />
                                            <span>{waitText}</span>
                                        </div>
                                    </div>

                                    {/* Route Segment Origin -> Destination */}
                                    <div className="mt-2 min-h-[2.8rem] flex flex-col justify-center">
                                        <div className="text-[13px] sm:text-sm font-bold text-neutral-900 dark:text-neutral-100 leading-snug line-clamp-1" title={origin}>
                                            {origin}
                                        </div>
                                        {destination ? (
                                            <div className="text-[11px] sm:text-xs font-semibold text-neutral-600 dark:text-neutral-300 flex items-center gap-1 mt-0.5 line-clamp-1" title={destination}>
                                                <ArrowRight className="w-3 h-3 text-[#E6781B] shrink-0 stroke-[2.5]" />
                                                <span className="truncate">{destination}</span>
                                            </div>
                                        ) : (
                                            <div className="text-[11px] text-neutral-600 dark:text-neutral-400 font-semibold mt-0.5">Direct Queue</div>
                                        )}
                                    </div>

                                    {/* Hero Metric: Count */}
                                    <div className="mt-2.5 pt-2 border-t border-neutral-100 dark:border-[#222735] flex items-baseline justify-between">
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-3xl sm:text-[32px] font-black text-neutral-900 dark:text-neutral-50 tracking-tight leading-none">
                                                {zone.count != null ? n(zone.count) : '—'}
                                            </span>
                                        </div>
                                        {!zone.hasCam ? (
                                            <span className="text-[11px] font-black px-1.5 py-0.5 rounded text-neutral-600 dark:text-neutral-400">no camera</span>
                                        ) : zone.count != null ? (
                                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 ${(zone.inflow ?? 0) > (zone.outflow ?? 0) + 5
                                                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                                                : (zone.outflow ?? 0) > (zone.inflow ?? 0) + 5
                                                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                                    : 'bg-neutral-100 dark:bg-white/5 text-neutral-600 dark:text-neutral-300'
                                                }`}>
                                                {(zone.inflow ?? 0) > (zone.outflow ?? 0) + 5 ? '▲ Building'
                                                    : (zone.outflow ?? 0) > (zone.inflow ?? 0) + 5 ? '▼ Clearing'
                                                        : '● Steady'}
                                            </span>
                                        ) : null}
                                    </div>

                                    {/* Consolidated Flow Telemetry Box with Entry Flow & Exit Flow */}
                                    <div className="mt-2.5 rounded-lg p-2.5 bg-neutral-50/90 dark:bg-[#10121a] border border-neutral-200/70 dark:border-[#222735]">
                                        {/* Entry Flow & Exit Flow columns: stacked label over figure so /min never wraps */}
                                        <div className="grid grid-cols-2 gap-2 pb-1.5 border-b border-neutral-200/60 dark:border-neutral-800">
                                            {/* Entry Flow column */}
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-[11px] font-bold text-[#C2620F] dark:text-amber-400 whitespace-nowrap flex items-center gap-1">
                                                    <TrendingUp className="w-3 h-3 stroke-[2.5] shrink-0" /> Entry Flow
                                                </span>
                                                <span className="font-black text-[#C2620F] dark:text-amber-400 text-sm sm:text-base tabular-nums mt-0.5 whitespace-nowrap">
                                                    {perMin(zone.inflow)}
                                                </span>
                                            </div>

                                            {/* Exit Flow column */}
                                            <div className="flex flex-col min-w-0 border-l border-neutral-200/80 dark:border-neutral-800/80 pl-2">
                                                <span className="text-[11px] font-bold text-[#BE123C] dark:text-rose-400 whitespace-nowrap flex items-center gap-1">
                                                    <TrendingDown className="w-3 h-3 stroke-[2.5] shrink-0" /> Exit Flow
                                                </span>
                                                <span className="font-black text-[#BE123C] dark:text-rose-400 text-sm sm:text-base tabular-nums mt-0.5 whitespace-nowrap">
                                                    {perMin(zone.outflow)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Full width In the last minute row */}
                                        <div className="pt-2 flex items-center justify-between text-xs">
                                            <span className="text-[11px] font-bold text-neutral-600 dark:text-neutral-400 whitespace-nowrap">In the last minute</span>
                                            <span className="font-black tabular-nums text-neutral-900 dark:text-neutral-100 whitespace-nowrap text-xs sm:text-[13px]">
                                                <span className="text-[#C2620F] dark:text-amber-400">▲ {n(zone.entryNow)}</span>
                                                <span className="mx-1 text-neutral-400 dark:text-neutral-600">·</span>
                                                <span className="text-[#BE123C] dark:text-rose-400">▼ {n(zone.exitNow)}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    {/* Went through today */}
                                    <div className="mt-2.5 pt-2 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between">
                                        <span className="text-neutral-600 dark:text-neutral-400 font-bold text-[11px] sm:text-xs">Went through today</span>
                                        <span className="font-black text-sm sm:text-base text-neutral-900 dark:text-neutral-100">{n(zone.passed)}</span>
                                    </div>

                                    {/* The zone's cameras - kept compact */}
                                    <div className="mt-1.5 flex items-center justify-between gap-2 text-xs font-bold text-neutral-600 dark:text-neutral-400">
                                        {([['Entry', zone.entryCams], ['Exit', zone.exitCams]] as ['Entry' | 'Exit', ZoneCam[]][]).map(([side, cams]) => (
                                            <div key={side} className="flex items-center gap-1 min-w-0">
                                                <span>{side}</span>
                                                {cams.length === 0 && <span className="text-neutral-600 dark:text-neutral-500 font-black">—</span>}
                                                {cams.map(cam => (
                                                    <button
                                                        key={cam.id}
                                                        type="button"
                                                        onClick={e => { e.stopPropagation(); setZoneCam({ zone: `${zone.label} · ${zone.name}`, zoneId: zone.id, side, cam }); }}
                                                        title={`${side} camera · ${noAddress(cam.name)}${cam.ip ? ` · ${cam.ip}` : ''} · ${cam.state === 'online' ? 'online' : cam.state === 'standby' ? 'view only' : 'no video'}`}
                                                        aria-label={`${zone.label} ${side} camera ${noAddress(cam.name)}`}
                                                        className={`p-1 rounded-md border transition-all cursor-pointer ${cam.state === 'online'
                                                            ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20'
                                                            : cam.state === 'standby'
                                                                ? 'text-sky-700 dark:text-sky-400 bg-sky-500/10 border-sky-500/30 hover:bg-sky-500/20'
                                                                : 'text-rose-700 dark:text-rose-400 bg-rose-500/10 border-rose-500/30 hover:bg-rose-500/20'
                                                            }`}
                                                    >
                                                        <Cctv className="w-3.5 h-3.5 stroke-[2.2]" />
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 4. HOLDING AREAS */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-2.5">
                {holdAreas.map((h: any) => {
                    const occPercent = h.cap ? Math.min(100, Math.round(((h.count ?? 0) / h.cap) * 100)) : 0;
                    return (
                        <div
                            key={h.id}
                            className="p-3 sm:p-3.5 rounded-2xl bg-white dark:bg-[#151720] border border-neutral-200/90 dark:border-[#262b37] shadow-xs hover:border-[#761D20]/40 dark:hover:border-neutral-600 hover:shadow-sm transition-all flex flex-col justify-between"
                        >
                            <div>
                                {/* Header: Title, Badge, Video wall & Capacity Count */}
                                <div className="flex items-start justify-between gap-2 pb-2 border-b border-neutral-100 dark:border-[#222735]">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="px-2 py-0.5 rounded-md text-xs sm:text-[13px] font-black bg-[#761D20] text-white dark:bg-white/20 shadow-2xs">
                                                {h.id}
                                            </span>
                                            <h3 className="text-sm sm:text-base font-black text-neutral-900 dark:text-neutral-100 tracking-tight">
                                                {h.name}
                                            </h3>
                                            {/* live video of all box cameras of this area: the app's Video wall page for this area (15 Oct 2026) */}
                                            {h.boxes_with_camera > 0 && (
                                                <a
                                                    href={`/api/holding/${h.id}/wall`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Live video wall of all box cameras"
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#761D20]/30 dark:border-amber-400/40 text-[#761D20] dark:text-amber-400 hover:bg-[#761D20] hover:text-white dark:hover:bg-amber-400 dark:hover:text-neutral-950 text-[11px] font-black transition-all shadow-2xs hover:scale-105"
                                                >
                                                    <Play className="w-2.5 h-2.5 fill-current" /> Video wall
                                                </a>
                                            )}
                                        </div>
                                        {/* Address & compartment count */}
                                        <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 flex items-start gap-1 mt-1.5 leading-snug">
                                            <MapPin className="w-3.5 h-3.5 mt-0.5 text-neutral-400 dark:text-neutral-500 shrink-0" />
                                            <span className="break-words">{h.location || ''} · {h.boxes?.length ?? 0} compartments</span>
                                        </div>
                                    </div>

                                    <div className="text-right shrink-0 pl-2">
                                        <div className="text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-50 leading-none tracking-tight">
                                            {n(h.count)} <span className="text-xs sm:text-sm font-bold text-neutral-400 dark:text-neutral-500">/{n(h.cap)}</span>
                                        </div>
                                        <div className="mt-1">
                                            <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                                                {h.count != null ? `${n(h.cap - h.count)} places free` : 'No cameras'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Slim Occupancy Visual Bar */}
                                <div className="mt-2 w-full bg-neutral-100 dark:bg-neutral-800/80 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className={`h-full transition-all duration-500 rounded-full ${occPercent > 90 ? 'bg-rose-500' : occPercent > 75 ? 'bg-orange-500' : occPercent > 50 ? 'bg-amber-500' : 'bg-emerald-500'
                                            }`}
                                        style={{ width: `${Math.max(occPercent, 2)}%` }}
                                    />
                                </div>

                                {/* High-tech Telemetry Strip (Waiting time, Coming in, Next to fill, Next to release) */}
                                <div className="mt-2.5 rounded-xl p-2 bg-neutral-50/90 dark:bg-[#10121a] border border-neutral-200/70 dark:border-[#222735] grid grid-cols-4 divide-x divide-neutral-200/60 dark:divide-neutral-800/80 text-xs">
                                    <div className="px-1.5 min-w-0">
                                        <span className="text-[10px] sm:text-[11px] font-bold text-neutral-500 dark:text-neutral-400 block truncate">Waiting time</span>
                                        <div className="font-black text-xs sm:text-[13px] text-neutral-900 dark:text-neutral-100 mt-0.5 truncate">{h.wait != null ? `${h.wait} min` : '—'}</div>
                                    </div>
                                    <div className="px-1.5 min-w-0 pl-2">
                                        <span className="text-[10px] sm:text-[11px] font-bold text-neutral-500 dark:text-neutral-400 block truncate">Coming in</span>
                                        <div className="font-black text-xs sm:text-[13px] text-neutral-900 dark:text-neutral-100 mt-0.5 truncate">{h.inflow != null ? `${n(h.inflow)} per min` : '—'}</div>
                                    </div>
                                    <div className="px-1.5 min-w-0 pl-2">
                                        <span className="text-[10px] sm:text-[11px] font-bold text-neutral-500 dark:text-neutral-400 block truncate">Next to fill</span>
                                        <div className="font-black text-xs sm:text-[13px] text-[#8A6508] dark:text-amber-400 mt-0.5 truncate">{h.fillNext ?? '—'}</div>
                                    </div>
                                    <div className="px-1.5 min-w-0 pl-2">
                                        <span className="text-[10px] sm:text-[11px] font-bold text-neutral-500 dark:text-neutral-400 block truncate">Next to release</span>
                                        <div className="font-black text-xs sm:text-[13px] text-[#C2620F] dark:text-orange-400 mt-0.5 truncate">{h.nextRelease ?? '—'}</div>
                                    </div>
                                </div>

                                {/* Entry and exit of the area (15 Oct 2026): what their cameras counted today and in the last 15 min */}
                                {(() => {
                                    const io = holdIo[h.id];
                                    const tile = (label: string, cams: any[] | undefined, today: number | null | undefined, m15: number | null | undefined, verb: string) => {
                                        const cam = (cams || [])[0];
                                        const live = cam?.state === 'online';
                                        return (
                                            <div className={`flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border ${cam ? 'border-[#761D20]/25 bg-[#FBF3EA] dark:bg-white/[0.04] dark:border-white/10 cursor-pointer hover:ring-2 hover:ring-[#761D20]/30 dark:hover:ring-orange-400/40 transition' : 'border-dashed border-neutral-300 dark:border-white/10 bg-neutral-50 dark:bg-white/[0.02]'}`}
                                                title={cam ? `${cam.name || cam.id} · ${cam.id} · ${live ? 'live' : cam.state}` : `No ${label.toLowerCase()} camera`}
                                                // the area's entry / exit camera live, in the same pop-up as the queue zones' cameras (15 Oct 2026)
                                                role={cam ? 'button' : undefined}
                                                onClick={cam ? () => setZoneCam({ zone: `${h.name} (${h.id})`, side: label === 'Exit' ? 'Exit' : 'Entry', cam: { id: cam.id, name: cam.name || cam.id, ip: cam.ip ?? null, state: cam.state } }) : undefined}>
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${!cam ? 'bg-neutral-300' : live ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                    <span className="text-[11px] font-black uppercase tracking-wide text-[#761D20] dark:text-orange-300">{label}</span>
                                                    {cam && <span className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 truncate">{cam.id}</span>}
                                                </div>
                                                {cam ? (
                                                    <div className="text-xs font-bold text-neutral-800 dark:text-neutral-100 mt-0.5 truncate">
                                                        {today != null ? `${n(today)} ${verb} today` : '—'}
                                                        {m15 != null && <span className="font-semibold text-neutral-500 dark:text-neutral-400"> · {n(m15)} in last 15 min</span>}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 mt-0.5">No camera</div>
                                                )}
                                            </div>
                                        );
                                    };
                                    return (
                                        <div className="mt-2.5 flex gap-1.5">
                                            {tile('Entry', io?.entry_cameras, io?.entered_today, io?.entered_15m, 'in')}
                                            {tile('Exit', io?.exit_cameras, io?.released_today, io?.released_15m, 'out')}
                                        </div>
                                    );
                                })()}

                                {/* Compartment boxes tactical grid */}
                                <div className="mt-1.5 grid grid-cols-5 sm:grid-cols-6 gap-1">
                                    {(h.boxes ?? []).map((b: any) => {
                                        const box: BoxItem = { id: boxLabel(b.id), current: b.count ?? null, max: b.cap };
                                        const colorClass = box.current == null ? NO_CAM_BOX : getBoxColorClasses(getBoxTier(box.current, box.max));
                                        return (
                                            <button
                                                key={b.id}
                                                onClick={() => setActiveBoxModal({ areaName: `${h.name} (${h.id})`, box, rawId: b.id })}
                                                className={`p-1 sm:p-1.5 rounded-lg border text-center transition-all cursor-pointer shadow-2xs hover:scale-105 hover:shadow-xs active:scale-95 flex flex-col justify-center items-center ${colorClass}`}
                                                title={box.current == null ? `${box.id}: no camera yet · capacity ${box.max}` : `${box.id}: ${box.current}/${box.max} devotees`}
                                            >
                                                <div className="text-[10px] sm:text-[11px] font-black leading-tight tracking-tight">{box.id}</div>
                                                <div className="text-[9px] sm:text-[10px] font-bold leading-tight opacity-90 mt-0.5">{box.current ?? '—'}/{box.max}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Footer: Compartment summary and camera readiness */}
                            <div className="mt-3 pt-2 border-t border-neutral-100 dark:border-[#222735] text-xs font-bold flex items-center justify-between text-neutral-500 dark:text-neutral-400">
                                <span className="flex items-center gap-1.5">
                                    <Layers className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                                    <span>{h.boxes?.length ?? 0} compartments</span>
                                </span>
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-black ${h.boxes_with_camera
                                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/25'
                                    : 'bg-neutral-100 dark:bg-white/5 text-neutral-500'
                                    }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${h.boxes_with_camera ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-400'}`} />
                                    {h.boxes_with_camera ?? 0} with camera
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 5. UNIVERSAL HOLDING STATUS COLOR LEGEND */}
            <div className="py-2 px-3.5 rounded-xl bg-white dark:bg-[#151720] border border-neutral-200/90 dark:border-[#262b37] shadow-xs flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="font-black text-neutral-900 dark:text-neutral-100 text-xs sm:text-[13px] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#761D20] dark:bg-amber-400" />
                    Compartment colours:
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-emerald-800 dark:text-emerald-300 font-bold text-xs">
                        <span className="w-2.5 h-2.5 rounded bg-emerald-500" />
                        <span>Available (&lt; 50%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/25 text-amber-800 dark:text-amber-300 font-bold text-xs">
                        <span className="w-2.5 h-2.5 rounded bg-amber-500" />
                        <span>Filling (50–75%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-500/10 border border-orange-500/25 text-orange-800 dark:text-orange-300 font-bold text-xs">
                        <span className="w-2.5 h-2.5 rounded bg-orange-500" />
                        <span>High (76–90%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-500/10 border border-rose-500/25 text-rose-800 dark:text-rose-300 font-bold text-xs">
                        <span className="w-2.5 h-2.5 rounded bg-rose-500" />
                        <span>Full (&gt; 90%)</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-neutral-500/10 border border-neutral-400/30 text-neutral-600 dark:text-neutral-400 font-bold text-xs">
                        <span className="w-2.5 h-2.5 rounded bg-neutral-400" />
                        <span>No camera yet</span>
                    </div>
                </div>
            </div>

            {/* MODAL: the live picture of one camera of a queue zone (opened from the Entry / Exit icons of its card) */}
            {zoneCam && (
                <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setZoneCam(null)}>
                    <div className="w-[1100px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#262b37] text-neutral-100 min-w-0">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${zoneCam.cam.state === 'online' ? 'bg-emerald-500' : zoneCam.cam.state === 'standby' ? 'bg-sky-500' : 'bg-rose-500'}`} />
                            <span className="text-base font-black shrink-0">{zoneCam.side} camera</span>
                            <span className="text-base font-bold truncate">· {noAddress(zoneCam.cam.name)}</span>
                            {zoneCam.cam.ip && <span className="text-xs sm:text-sm font-mono font-semibold text-sky-300 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 shrink-0">{zoneCam.cam.ip}</span>}
                            <span className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 truncate hidden sm:inline">{zoneCam.zone}</span>
                            <button onClick={() => setZoneCam(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="aspect-video bg-black">
                            <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(zoneCam.cam.id)}/snapshot.jpg`} alt={zoneCam.cam.id}
                                size="full" maxFps={12} className="w-full h-full object-contain" />
                        </div>
                        {/* the picture above already carries the detection boxes and the counting line; these are its figures */}
                        <ZoneCamStats camId={zoneCam.cam.id} />
                        {/* the zone's own figure, the same number as its card, and what it is made of (13 Oct 2026) */}
                        {(() => {
                            const zi = (api?.zones ?? []).findIndex((x: any) => x.id === zoneCam.zoneId);
                            const z: any = zi >= 0 ? api.zones[zi] : null;
                            if (!z) return null;
                            const parts = [
                                z.visible_floor != null ? `cameras see ${n(z.visible_floor)}` : null,
                                z.correction ? `correction ${z.correction > 0 ? '+' : ''}${n(z.correction)}` : null,
                            ].filter(Boolean);
                            return (
                                <div className="px-4 py-2.5 border-t border-[#262b37] bg-[#141821] text-neutral-200 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                                    <span className="text-sm font-black">Q{zi + 1} now: <span className="text-xl">{n(z.count)}</span></span>
                                    {parts.length > 0 && <span className="text-xs text-neutral-600 dark:text-neutral-400">({parts.join(' · ')})</span>}
                                    <span className="text-xs text-neutral-300">entered today <b>{n(z.entered)}</b></span>
                                    <span className="text-xs text-neutral-300">exited today <b>{n(z.exited)}</b></span>
                                    <span className="text-[11px] text-neutral-600 dark:text-neutral-400">same number as the Q{zi + 1} card · all of the zone's cameras together</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* MODAL: BOX DETAILS & CONTROL POPUP */}
            {showDarshanCompleted && <DarshanCompletedDetails onClose={() => setShowDarshanCompleted(false)} />}
            {activeBoxModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-3">
                    <div className="w-full max-w-2xl max-h-[94vh] overflow-y-auto rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#272e3d] shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between pb-2.5 border-b border-neutral-200 dark:border-[#272e3d]">
                            <div>
                                <h3 className="text-base sm:text-lg font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-1.5">
                                    <Warehouse className="w-4.5 h-4.5 text-emerald-500 stroke-[2.5]" />
                                    <span>Compartment {activeBoxModal.box.id}</span>
                                </h3>
                                <p className="text-xs sm:text-sm font-bold text-neutral-600 dark:text-neutral-300 mt-0.5">
                                    {activeBoxModal.areaName}
                                </p>
                            </div>
                            <button
                                onClick={() => setActiveBoxModal(null)}
                                className="p-1 rounded-lg text-neutral-600 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-white cursor-pointer"
                            >
                                <X className="w-4 h-4 stroke-[2.5]" />
                            </button>
                        </div>

                        <div className="mt-3 space-y-2.5">
                            {/* Live video of the box's camera */}
                            {activeBoxModal.rawId && <BoxVideo boxId={activeBoxModal.rawId} />}
                            {/* Progress & Occupancy (boxNow: live figure, see liveBoxCount) */}
                            <div className="p-2.5 rounded-lg bg-neutral-50 dark:bg-[#14161e] border border-neutral-200 dark:border-[#272e3d]">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-xs sm:text-sm font-extrabold text-neutral-600 dark:text-neutral-300">Occupancy</span>
                                    <span className="text-2xl sm:text-3xl font-black text-neutral-900 dark:text-neutral-100">
                                        {boxNow ?? '—'} <span className="text-sm font-bold text-neutral-600 dark:text-neutral-400">/ {activeBoxModal.box.max}</span>
                                    </span>
                                </div>
                                <div className="mt-1.5 w-full bg-neutral-200 dark:bg-neutral-800 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-2 rounded-full bg-emerald-500"
                                        style={{ width: `${Math.min(100, ((boxNow ?? 0) / (activeBoxModal.box.max || 1)) * 100)}%` }}
                                    />
                                </div>
                                <div className="mt-1.5 flex justify-between text-xs sm:text-sm font-bold text-neutral-600 dark:text-neutral-300">
                                    <span>{boxNow != null ? `Fill Rate: ${Math.round((boxNow / (activeBoxModal.box.max || 1)) * 100)}%` : 'No camera in this box yet'}</span>
                                    <span>{boxNow != null ? `Remaining: ${activeBoxModal.box.max - boxNow}` : `Capacity: ${activeBoxModal.box.max}`}</span>
                                </div>
                            </div>

                            {/* Status details */}
                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm font-bold">
                                <div className="p-2.5 rounded-lg bg-neutral-50 dark:bg-[#14161e] border border-neutral-200 dark:border-[#272e3d]">
                                    <span className="text-neutral-600 dark:text-neutral-400">Estimated Dwell</span>
                                    <div className="font-black text-neutral-900 dark:text-neutral-100 mt-0.5">—</div>
                                </div>
                                <div className="p-2.5 rounded-lg bg-neutral-50 dark:bg-[#14161e] border border-neutral-200 dark:border-[#272e3d]">
                                    <span className="text-neutral-600 dark:text-neutral-400">Gate Release</span>
                                    <div className="font-black text-neutral-600 dark:text-neutral-400 mt-0.5">Not connected</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-3.5 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setActiveBoxModal(null)}
                                className="px-3 py-1.5 rounded-lg text-sm font-bold border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    alert(`Gate control is not connected to this console yet (box ${activeBoxModal.box.id}).`);
                                    setActiveBoxModal(null);
                                }}
                                className="px-3 py-1.5 rounded-lg text-sm font-black bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm cursor-pointer"
                            >
                                Release Box to Queue
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OverviewPage;
